import { GUIDE_METADATA } from '@/data/guide-slugs'
import { GRASS_CHARACTERISTICS, resolveGrassDisplayName } from '@/data/grass-characteristics'

const COOL_FALLBACK = 'kentucky-bluegrass'
const WARM_FALLBACK = 'bermudagrass'

export function grassTypeToGuideSlug(displayName: string | null | undefined): string | null {
  if (!displayName) return null
  // Resolve model-output shorthand ("Bermuda", "St Augustine") to the canonical
  // display name first — without this, alias results silently miss every
  // slug-keyed surface (guides, reference photos) despite a confident ID.
  const trimmed = resolveGrassDisplayName(displayName.trim())
  // Match by display name, grass key, OR the canonical guide slug itself, so a
  // caller that already holds the slug (e.g. the city-SEO pages) round-trips it
  // instead of getting dropped. Slug-matching only adds matches; it can't change
  // an existing name/key match.
  const match = GUIDE_METADATA.find(
    (g) => g.displayName === trimmed || g.grassKey === trimmed || g.slug === trimmed,
  )
  return match?.slug ?? null
}

// Reverse of grassTypeToGuideSlug: turn a guide slug (or a display name / grass
// key) back into the canonical display name. Returns null when unrecognized so
// callers can fall back to a grass-less label instead of printing a raw slug
// (e.g. the soil-test analyzer, whose parse API stores grass as a slug).
export function guideSlugToGrassName(value: string | null | undefined): string | null {
  if (!value) return null
  const match = GUIDE_METADATA.find(
    (g) => g.slug === value || g.displayName === value || g.grassKey === value,
  )
  return match?.displayName ?? null
}

export function grassTypeToFallbackSlug(displayName: string | null | undefined): string {
  const exact = grassTypeToGuideSlug(displayName)
  if (exact) return exact
  if (!displayName) return COOL_FALLBACK
  const characteristics = GRASS_CHARACTERISTICS[displayName.trim()]
  if (characteristics?.season === 'Warm') return WARM_FALLBACK
  return COOL_FALLBACK
}
